import Button from '@components/Button';
import Checkbox from '@components/Checkbox';
import { showConfirmModal } from '@components/ConfirmModal';
import Tip from '@components/Tip';
import { TipWrapper } from '@components/TipWrapper';
import Title from '@components/Title';
import IconExit from '@components/icons/IconExit';
import { $tourDataApi } from '@stores/dataStores';
import { $GateSandtableBatchMode } from '@stores/sandTableStores';
import type { STMap, STMapMarker } from '@types';
import getSelectedStatus from '@utils/getSelectedStatus';
import { useGate } from 'effector-react';
import { useCallback, useEffect } from 'react';
import { Scrollbars } from 'react-custom-scrollbars';
import { useImmer } from 'use-immer';
import SidebarHeader from '../../sidebar/components/SidebarHeader';
import { stEditorAmap } from '../STAMap';
import { deleteMarkers } from '../actions/deleteMarkers';
import { onMarkerClickBatchMode } from '../actions/onMarkerClick';
import { MapMarkerIcon } from '../components/MarkerIcon';
import { MapMarkerSubtitle } from '../components/MarkerSubtitle';
import { showSandTableCopyToModal } from '../components/SandTableCopyToModal';
import { newMarkerId } from '../utils/newMarkerId';

interface Props {
  data: STMap;
  onQuitClick: () => void;
}

export function SidebarSandTableMapBatch({ data, onQuitClick }: Props) {
  const { markers } = data;
  const [selected, setSelected] = useImmer<Set<string>>(new Set());

  useGate($GateSandtableBatchMode, true);

  useEffect(() => {
    return onMarkerClickBatchMode.watch((itemId) => {
      handleBatchItemClick(itemId);
    });
  }, []);

  const toggleSelected = (id: string) => {
    setSelected((draft) => {
      if (draft.has(id)) {
        draft.delete(id);
      } else {
        draft.add(id);
      }
    });
  };

  const handleBatchItemClick = useCallback((markerId: string) => {
    toggleSelected(markerId);
    // stEditorAmap.centerMarker(markerData);
  }, []);

  const handleSelectAll = () => {
    if (selected.size < markers.length) {
      setSelected(new Set(markers.map((x) => x.id)));
    } else {
      setSelected((draft) => {
        draft.clear();
      });
    }
  };

  const handleCopyTo = async () => {
    const { idList } = await showSandTableCopyToModal({
      currentId: data.id,
      type: data.type,
    });
    if (!idList) return;
    const selectedMarkers = markers.filter((x) => selected.has(x.id));
    $tourDataApi.update((draft) => {
      idList.forEach((id) => {
        const target = draft.sandBox.find((x) => x.id === id) as STMap;
        if (target) {
          target.markers.push(
            ...selectedMarkers.map((x) => ({ ...x, id: newMarkerId() }))
          );
        }
      });
    });
  };

  const handleBatchDelete = async () => {
    const { confirmed } = await showConfirmModal({
      title: '操作确认',
      content: (
        <>
          <div className="mb-5 text-xl">{`确认删除选中的${selected.size}个标记点吗？`}</div>
          <div className="text-center">删除后，标记点不能被恢复。</div>
        </>
      ),
    });
    if (!confirmed) return;
    deleteMarkers({
      sandTableId: data.id,
      markerIds: selected,
    });
    selected.forEach((id) => {
      stEditorAmap.removeMarker(id);
    });
    setSelected((draft) => {
      draft.clear();
    });
  };

  return (
    <>
      <div className="absolute inset-0 flex flex-col">
        <SidebarHeader
          title={
            <div className="flex">
              <Title>标记点</Title>
              <Tip className="ml-10 self-end text-base">批量操作</Tip>
            </div>
          }
          btns={
            <TipWrapper text="退出操作" placement="bottom" size="sm">
              <IconExit className="focus:outline-none" onClick={onQuitClick} />
            </TipWrapper>
          }
        />
        <div
          className="mb-10 flex h-35 cursor-pointer items-center rounded bg-black-#34 px-10"
          onClick={handleSelectAll}
        >
          <Checkbox
            status={getSelectedStatus(selected.size, markers.length)}
            onClick={handleSelectAll}
          />
          <div className="ml-5">全选</div>
          <div className="ml-auto text-sm text-black-gray ">{`${selected.size}/${markers.length}`}</div>
        </div>
        {markers.length > 0 ? (
          <div className="relative flex-1">
            <Scrollbars>
              {markers.map((x) => (
                <Item
                  key={x.id}
                  data={x}
                  isSelected={selected.has(x.id)}
                  onClick={handleBatchItemClick}
                />
              ))}
            </Scrollbars>
          </div>
        ) : (
          <Tip className="mt-5 flex-1 text-center text-base">
            没有标记点可批量操作
          </Tip>
        )}
        <Button
          className="block w-full"
          disabled={selected.size === 0}
          onClick={handleCopyTo}
        >
          复制到...
        </Button>
        <Button
          scheme="dark"
          className="mt-5 block w-full"
          disabled={selected.size === 0}
          onClick={handleBatchDelete}
        >
          删除
        </Button>
        <Button
          scheme="darkest"
          className="mt-5 block w-full"
          onClick={onQuitClick}
        >
          退出
        </Button>
      </div>
    </>
  );
}

type ItemProps = {
  data: STMapMarker;
  isSelected: boolean;
  onClick: (id: string) => void;
};

function Item({ data, isSelected, onClick }: ItemProps) {
  return (
    <div
      className="mb-10 flex h-35 cursor-pointer items-center rounded bg-black-#34 px-10"
      onClick={() => onClick(data.id)}
    >
      <Checkbox
        className="flex-shrink-0 mr-10"
        checked={isSelected}
        onClick={() => onClick(data.id)}
      />
      <MapMarkerIcon marker={data} />
      <div className="mx-10 truncate text-base text-white" title={data.title}>
        {data.title}
      </div>
      <MapMarkerSubtitle className="ml-auto" marker={data} />
    </div>
  );
}
